import React, { useState } from 'react';
import { BookOpen, FileText, Code, Database, Users, FolderOpen, Bookmark, Search, Filter, Layers, ExternalLink, ChevronRight, ChevronDown, Home, RefreshCw, Map } from 'lucide-react';
import ComponentTypeIcon from '../components/ComponentTypeIcon';

const DocumentationPage: React.FC = () => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['getting-started']));

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <BookOpen className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">
              Welcome to PatternLab Dashboard
            </h3>
            <p className="text-blue-700 dark:text-blue-300">
              This dashboard provides a comprehensive view of your PatternLab components, projects, and people. 
              Use the navigation above to explore different sections of the application.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                             <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                 <Layers className="w-4 h-4 mr-2" />
                 Dashboard Overview
               </h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Get a bird's eye view of all your components, recent activity, and system status.
              </p>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <Search className="w-4 h-4 mr-2" />
                Search & Filter
              </h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Quickly find components using the search bar and filter by type, project, or other criteria.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'navigation',
      title: 'Navigation & Layout',
      icon: <Layers className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <Home className="w-4 h-4 mr-2" />
                Dashboard
              </h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Main overview page with component grid, search, and filtering options.
              </p>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <FolderOpen className="w-4 h-4 mr-2" />
                Projects
              </h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Browse projects and view their associated components in detail.
              </p>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <Users className="w-4 h-4 mr-2" />
                People
              </h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                View team members and their contributions to components and projects.
              </p>
            </div>
          </div>
          
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Meta Navigation</h4>
            <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
              The right side of the header contains user controls and settings:
            </p>
            <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1 ml-4">
              <li>• <strong>Theme Toggle:</strong> Switch between light and dark modes</li>
              <li>• <strong>User Info:</strong> View your profile and authentication status</li>
              <li>• <strong>Sign Out:</strong> Log out of the application</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'routes',
      title: 'Application Routes',
      icon: <Map className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">
              Route Structure & Navigation
            </h3>
            <p className="text-blue-700 dark:text-blue-300">
              The PatternLab Dashboard uses a protected routing system that requires authentication for all pages except the login screen.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <Home className="w-4 h-4 mr-2" />
                Main Routes
              </h4>
              <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-2">
                <li><strong>/</strong> - Dashboard (main overview)</li>
                <li><strong>/projects</strong> - Projects listing page</li>
                <li><strong>/projects/:projectId</strong> - Individual project details</li>
                <li><strong>/projects/:projectId/components/:componentName</strong> - Component details within a project</li>
                <li><strong>/people</strong> - Team members listing</li>
                <li><strong>/people/:personName</strong> - Individual person details</li>
                <li><strong>/bookmarks</strong> - Saved bookmarks page</li>
                <li><strong>/documentation</strong> - This documentation page</li>
              </ul>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <Database className="w-4 h-4 mr-2" />
                Development Routes
              </h4>
              <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-2">
                <li><strong>/api-test</strong> - API testing interface (dev mode only)</li>
                <li><strong>/login</strong> - Authentication splash screen</li>
                <li><strong>*</strong> - Catch-all route (redirects to dashboard)</li>
              </ul>
            </div>
          </div>
          
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Authentication & Access Control</h4>
            <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
              All routes are protected by authentication except for the login route:
            </p>
            <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1 ml-4">
              <li>• <strong>Unauthenticated users:</strong> Redirected to splash screen with Google sign-in</li>
              <li>• <strong>Authenticated users:</strong> Can access all protected routes</li>
              <li>• <strong>Domain restrictions:</strong> Only users from <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">logic-joe.com</code> can sign in</li>
              <li>• <strong>Undefined routes:</strong> Automatically redirect to dashboard</li>
            </ul>
          </div>
          
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Route Behavior</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h5 className="font-medium text-gray-900 dark:text-white mb-2">Authenticated Users</h5>
                <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1">
                  <li>• All routes accessible</li>
                  <li>• <code>/login</code> redirects to dashboard</li>
                  <li>• Undefined routes redirect to dashboard</li>
                </ul>
              </div>
              <div>
                <h5 className="font-medium text-gray-900 dark:text-white mb-2">Unauthenticated Users</h5>
                <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1">
                  <li>• All routes show splash screen</li>
                  <li>• Must sign in with Google</li>
                  <li>• Domain validation required</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'components',
      title: 'Working with Components',
      icon: <Code className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-2">
              Component Types
            </h3>
            <p className="text-green-700 dark:text-green-300 mb-4">
              Components are organized by type (atoms, molecules, organisms, templates, pages) 
              following atomic design principles.
            </p>
            
            {/* Visual examples of component types */}
            <div className="grid grid-cols-5 gap-3 mt-4">
              <div className="text-center">
                <div className="flex justify-center mb-2">
                  <ComponentTypeIcon type="atoms" size="lg" />
                </div>
                <p className="text-xs font-medium text-green-800 dark:text-green-200">Atoms</p>
                <p className="text-xs text-green-700 dark:text-green-300">Basic building blocks</p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-2">
                  <ComponentTypeIcon type="molecules" size="lg" />
                </div>
                <p className="text-xs font-medium text-green-800 dark:text-green-200">Molecules</p>
                <p className="text-xs text-green-700 dark:text-green-300">Simple combinations</p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-2">
                  <ComponentTypeIcon type="organisms" size="lg" />
                </div>
                <p className="text-xs font-medium text-green-800 dark:text-green-200">Organisms</p>
                <p className="text-xs text-green-700 dark:text-green-300">Complex structures</p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-2">
                  <ComponentTypeIcon type="templates" size="lg" />
                </div>
                <p className="text-xs font-medium text-green-800 dark:text-green-200">Templates</p>
                <p className="text-xs text-green-700 dark:text-green-300">Layout structures</p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-2">
                  <ComponentTypeIcon type="pages" size="lg" />
                </div>
                <p className="text-xs font-medium text-green-800 dark:text-green-200">Pages</p>
                <p className="text-xs text-green-700 dark:text-green-300">Complete views</p>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <Search className="w-4 h-4 mr-2" />
                Search & Filter
              </h4>
              <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1">
                <li>• Use the search bar to find components by name</li>
                <li>• Filter by component type using the type buttons</li>
                <li>• Filter by project or other criteria</li>
                <li>• Clear all filters to reset the view</li>
              </ul>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <Bookmark className="w-4 h-4 mr-2" />
                Bookmarks
              </h4>
              <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1">
                <li>• Click the bookmark icon to save components</li>
                <li>• Access all bookmarked components in the Bookmarks page</li>
                <li>• Bookmarks persist across sessions</li>
                <li>• Quick access to frequently used components</li>
              </ul>
            </div>
          </div>
          
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Component Details</h4>
            <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
              Click on any component to view detailed information:
            </p>
            <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1 ml-4">
              <li>• Component metadata and properties</li>
              <li>• Project associations</li>
              <li>• Related components</li>
              <li>• Usage examples and documentation</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'projects',
      title: 'Project Management',
      icon: <FolderOpen className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Project Overview</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                View all projects and their associated components. Each project shows:
              </p>
              <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1 ml-4 mt-2">
                <li>• Project name and description</li>
                <li>• Component count and types</li>
                <li>• Team members involved</li>
                <li>• Project status and timeline</li>
              </ul>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Project Details</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Click on any project to see detailed information:
              </p>
              <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1 ml-4 mt-2">
                <li>• Complete component list</li>
                <li>• Filtering and sorting options</li>
                <li>• Project documentation</li>
                <li>• Team collaboration tools</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'people',
      title: 'Team & Collaboration',
      icon: <Users className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Team Overview</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                View all team members and their contributions:
              </p>
              <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1 ml-4 mt-2">
                <li>• Team member profiles</li>
                <li>• Component ownership</li>
                <li>• Project involvement</li>
                <li>• Contribution statistics</li>
              </ul>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Collaboration</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Tools for team collaboration:
              </p>
              <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1 ml-4 mt-2">
                <li>• Component sharing</li>
                <li>• Project assignments</li>
                <li>• Communication channels</li>
                <li>• Feedback and reviews</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'features',
      title: 'Advanced Features',
      icon: <Database className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <Bookmark className="w-4 h-4 mr-2" />
                Bookmarking System
              </h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Save frequently used components for quick access:
              </p>
              <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1 ml-4 mt-2">
                <li>• One-click bookmarking</li>
                <li>• Organized bookmark collections</li>
                <li>• Cross-project access</li>
                <li>• Persistent storage</li>
              </ul>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <Filter className="w-4 h-4 mr-2" />
                Advanced Filtering
              </h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Powerful filtering and search capabilities:
              </p>
              <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1 ml-4 mt-2">
                <li>• Multi-criteria filtering</li>
                <li>• Type-based organization</li>
                <li>• Project-specific views</li>
                <li>• Saved filter presets</li>
              </ul>
            </div>
          </div>
          
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Development Mode</h4>
            <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
              Enable development mode by adding <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-sm">?dev=true</code> to the URL:
            </p>
            <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1 ml-4">
              <li>• API testing interface</li>
              <li>• Debug information</li>
              <li>• System status monitoring</li>
              <li>• Development tools and utilities</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'feedback',
      title: 'Feedback & Support',
      icon: <FileText className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">
              We Value Your Feedback
            </h3>
            <p className="text-blue-700 dark:text-blue-300">
              This application is in active development. Your feedback helps us improve the user experience.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Report Bugs</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                Found a bug or issue? Use the Report Bug button in the development banner:
              </p>
              <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1 ml-4">
                <li>• Describe the problem clearly</li>
                <li>• Include steps to reproduce</li>
                <li>• Mention your browser and OS</li>
                <li>• Attach screenshots if helpful</li>
              </ul>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Feature Requests</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                Have ideas for improvements? Reach out via:
              </p>
              <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1 ml-4">
                <li>• Slack: Peter Amelang</li>
                <li>• Email: p.amelang@logic-joe.com</li>
                <li>• Include use case details</li>
                <li>• Describe expected benefits</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'cache',
      title: 'Performance & Caching',
      icon: <Database className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">
              Smart Caching System
            </h3>
            <p className="text-blue-700 dark:text-blue-300">
              The dashboard uses intelligent local caching to improve performance and reduce API calls. 
              Data is automatically cached and refreshed in the background.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <Database className="w-4 h-4 mr-2" />
                Cache Benefits
              </h4>
              <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1">
                <li>• Faster page loads with cached data</li>
                <li>• Reduced API calls and server load</li>
                <li>• Better offline experience</li>
                <li>• Automatic background refresh</li>
              </ul>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <RefreshCw className="w-4 h-4 mr-2" />
                Cache Management
              </h4>
              <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-1">
                <li>• Projects: 10 minutes cache duration</li>
                <li>• Components: 5 minutes cache duration</li>
                <li>• Automatic cleanup of old data</li>
                <li>• Manual refresh options available</li>
              </ul>
            </div>
          </div>
          
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Cache Status & Controls</h4>
            <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
              Caching functionality has been temporarily removed from this version.
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Documentation
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Complete guide to using the PatternLab Dashboard
              </p>
            </div>
          </div>
          
          {/* AI Generated Content Warning */}
          <div className="bg-amber-50 dark:bg-amber-900/30 border-2 border-amber-300 dark:border-amber-600 rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                  <span className="text-amber-900 text-lg font-bold">⚠️</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-amber-800 dark:text-amber-200 mb-2">
                  AI-Generated Content Warning
                </h3>
                <p className="text-amber-700 dark:text-amber-300 text-base leading-relaxed">
                  <strong>Important:</strong> All information in this documentation view has been automatically generated by AI and may not accurately reflect the current state of your specific PatternLab Dashboard implementation.
                </p>
                <p className="text-amber-700 dark:text-amber-300 text-base leading-relaxed mt-2">
                  This documentation will be personalized and customized for your specific setup in future updates. For the most accurate and up-to-date information about your dashboard, please refer to your project-specific documentation or contact your development team.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              <strong>Quick Tip:</strong> Use the navigation menu above to jump between different sections of the documentation. 
              Each section can be expanded or collapsed for easier navigation.
            </p>
          </div>
        </div>

        {/* Documentation Sections */}
        <div className="space-y-4">
          {sections.map((section) => (
            <div key={section.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="text-primary-600 dark:text-primary-400">
                    {section.icon}
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {section.title}
                  </h2>
                </div>
                {expandedSections.has(section.id) ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {expandedSections.has(section.id) && (
                <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-700">
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Need More Help?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              If you can't find what you're looking for in this documentation, don't hesitate to reach out.
            </p>
            <div className="flex items-center justify-center space-x-4">
              <a
                href="https://logic-joe.slack.com/team/p.amelang"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Contact on Slack
              </a>
              <a
                href="mailto:p.amelang@logic-joe.com?subject=PatternLab%20Dashboard%20Help&body=Hi%20Peter%2C%0A%0AI%20need%20help%20with%20the%20PatternLab%20Dashboard%3A%0A%0A"
                className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Send Email
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationPage;
